import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ClipboardCheck, ChevronRight, PackageCheck, PackageOpen, ShieldAlert } from "lucide-react";
import { api } from "../../api/client";
import { unwrapArray } from "../../lib/api-envelope";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import FieldPageChrome from "../../components/field/FieldPageChrome";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { DhEmpty } from "../downhole/DownholeBits";
import {
  DASH,
  GradeBadge,
  TypeChip,
  gradeSkin,
  normalizeInspection,
  type InspectionApiRow,
  type InspectionType,
} from "../../components/rentals/InspectionBits";
import { NewInspectionDialog } from "../../components/rentals/NewInspectionDialog";

/**
 * Rental Inspections, /rentals/inspections (#815 Inspections): a full-width
 * register. A row opens /rentals/inspections/:id; New Inspection picks the
 * asset, order and type here and fills the checklist on
 * /rentals/inspections/new. It was a split pane with the detail and the draft
 * beside the list.
 */

// SafetyRecordsScreen reads rental inspections through this; it moved to
// InspectionBits with the split pane, and is re-exported so that screen is
// untouched.
export { normalizeInspection };


/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * `surface="field"` is the rental tech's Inspections tile (the /my-work field
 * tree, fe#815): the casing field header and back arrow, New Inspection first,
 * then the same chips, search and list — no office band and no stat strip. The
 * office tree passes nothing and renders exactly as before.
 */
export default function RentalInspections({ surface = "office" }: { surface?: "office" | "field" } = {}) {
  const isField = surface === "field";
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();

  const inspectionsQuery = useQuery({
    // The zone is in the key because the rows carry a time formatted in it.
    queryKey: ["rentals", "inspections", timeZone],
    queryFn: async () => {
      const res = await api.rentals.inspections.list();
      return unwrapArray<InspectionApiRow>(res.data).map((row) => normalizeInspection(row, timeZone));
    },
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failed = queryLoadFailure(inspectionsQuery) != null;
  const loading = inspectionsQuery.isLoading && !failed;
  const inspections = useMemo(() => inspectionsQuery.data ?? [], [inspectionsQuery.data]);
  const [typeFilter, setTypeFilter] = useState<"all" | InspectionType>("all");
  const [searchQuery, setSearchQuery] = useState("");

  /* new-inspection dialog */
  const [dialogOpen, setDialogOpen] = useState(false);
  const openNewDialog = () => setDialogOpen(true);

  const filtered = inspections.filter((ins) => {
    if (typeFilter !== "all" && ins.type !== typeFilter) return false;
    const q = searchQuery.toLowerCase();
    return (ins.asset_name ?? "").toLowerCase().includes(q) || (ins.performed_by ?? "").toLowerCase().includes(q);
  });

  const typeCount = (t: "all" | InspectionType) =>
    failed || !inspectionsQuery.data ? null : inspections.filter((i) => t === "all" || i.type === t).length;
  /** Fails: the grade that keeps an asset in the yard. An unreadable grade is not counted. */
  const failCount = failed || !inspectionsQuery.data ? null : inspections.filter((i) => i.overall === "fail").length;

  const TYPE_FILTERS: { key: "all" | InspectionType; label: string }[] = [
    { key: "all", label: "All" },
    { key: "pre_delivery", label: "Pre-Delivery" },
    { key: "post_return", label: "Post-Return" },
  ];

  const open = (id: string) => navigate(`/rentals/inspections/${encodeURIComponent(id)}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open(id);
    }
  };

  const newInspection = (
    <button type="button" onClick={openNewDialog} className="fi-action fi-action--standard fi-action--primary">
      <Plus className="size-4" aria-hidden="true" />
      New Inspection
    </button>
  );

  const body = (
    <>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
              {TYPE_FILTERS.map((f) => {
                const active = typeFilter === f.key;
                const n = typeCount(f.key);
                return (
                  <button
                    key={f.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setTypeFilter(f.key)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? "bg-fi-amber text-fi-amber-ink"
                        : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                    }`}
                  >
                    {f.label}
                    {!loading && n != null && (
                      <span className={`tabular-nums ${active ? "text-fi-amber-ink" : "text-fieldiq-muted"}`}>{n}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="relative lg:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search inspections..."
                aria-label="Search inspections"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </div>
          </div>

          {/* The register. The amber edge is on this panel and nowhere else. */}
          <section
            aria-labelledby="inspection-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Inspection log</p>
              <h2 id="inspection-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                Rental asset inspections
              </h2>
            </header>
            {failed ? (
              <LoadFailureNotice error={queryFailureError(inspectionsQuery)} subject="inspections" />
            ) : loading ? (
              <div className="space-y-3 p-4" aria-hidden="true">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <DhEmpty>{inspections.length === 0 ? "No inspections yet" : "No inspections match these filters"}</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[900px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Asset</th>
                        <th scope="col" className="px-4 py-3">Type</th>
                        <th scope="col" className="px-4 py-3">Overall</th>
                        <th scope="col" className="px-4 py-3">When</th>
                        <th scope="col" className="px-4 py-3">Inspected by</th>
                        <th scope="col" className="px-4 py-3">Work order</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((ins) => (
                        <tr
                          key={ins.id}
                          data-testid="inspection-row"
                          role="button"
                          aria-label={`${ins.asset_name} ${ins.asset_tag}`}
                          tabIndex={0}
                          onClick={() => open(ins.id)}
                          onKeyDown={rowKeys(ins.id)}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${gradeSkin(ins.overall).rail}`}
                        >
                          <td className="min-w-[12rem] px-4 py-3">
                            <p className="text-sm font-bold text-fieldiq-text">{ins.asset_name}</p>
                            <p className="font-mono text-[11px] text-fieldiq-muted">{ins.asset_tag}</p>
                          </td>
                          <td className="px-4 py-3"><TypeChip type={ins.type} /></td>
                          <td className="px-4 py-3"><GradeBadge grade={ins.overall} /></td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{ins.when}</td>
                          <td className="min-w-[10rem] px-4 py-3 text-sm text-fieldiq-text">{ins.performed_by}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{ins.work_order_id ? "Opened" : DASH}</td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {filtered.map((ins) => (
                    <div
                      key={ins.id}
                      data-testid="inspection-card"
                      role="button"
                      aria-label={`${ins.asset_name} ${ins.asset_tag}`}
                      tabIndex={0}
                      onClick={() => open(ins.id)}
                      onKeyDown={rowKeys(ins.id)}
                      className={`cursor-pointer rounded-fi-panel border border-l-4 border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10 ${gradeSkin(ins.overall).rail}`}
                    >
                      <div className="mb-1.5 flex flex-wrap items-center gap-2">
                        <TypeChip type={ins.type} />
                        <GradeBadge grade={ins.overall} />
                      </div>
                      <p className="truncate text-sm font-bold text-fieldiq-text">{ins.asset_name}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {ins.asset_tag} &middot; {ins.when}
                      </p>
                      <p className="text-xs text-fieldiq-muted">By {ins.performed_by}</p>
                    </div>
                  ))}
                </div>
                <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                  Showing {filtered.length} of {inspections.length} inspections
                </p>
              </>
            )}
          </section>
    </>
  );

  return (
    <>
    {isField ? (
      <FieldPageChrome
        testId="rental-inspections-page"
        title="Inspections"
        onBack={() => navigate("/my-work")}
        action={newInspection}
      >
        {body}
      </FieldPageChrome>
    ) : (
    <div data-testid="rental-inspections-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals"
        title="Inspections"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={newInspection}
        /* Tallies of the loaded list: GET /inspections is unpaginated and no
           endpoint returns these figures. */
        stats={[
          { icon: <ClipboardCheck className="size-4" aria-hidden="true" />, label: "Inspections", sub: "On record", tone: "neutral", loading, value: displayValue(typeCount("all")) },
          { icon: <PackageOpen className="size-4" aria-hidden="true" />, label: "Pre-Delivery", sub: "Before going out", tone: "info", loading, value: displayValue(typeCount("pre_delivery")) },
          { icon: <ShieldAlert className="size-4" aria-hidden="true" />, label: "Defects", sub: "Failed", tone: "accent", loading, value: displayValue(failCount) },
          { icon: <PackageCheck className="size-4" aria-hidden="true" />, label: "Post-Return", sub: "After coming back", tone: "success", loading, value: displayValue(typeCount("post_return")) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {body}
        </div>
      </section>
    </div>
    )}

      <NewInspectionDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
